// Web theme context — light/dark switching for the hybrid design system.
//
// Mirrors the mobile app's ThemeContext (frontend_mobile/.../contexts/
// ThemeContext.tsx): persists the choice and exposes a toggle. On web the
// actual colors come from CSS custom properties in src/styles/tokens.css; this
// context just sets `data-theme` on <html>, which flips which token block wins.
//
// Default resolution order: saved choice → system prefers-color-scheme → light.

import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ThemeContext = createContext(null);

const STORAGE_KEY = '@tudu:web-theme';

function getInitialTheme() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (_) {
    /* localStorage unavailable (private mode / SSR) — fall through */
  }
  if (typeof window !== 'undefined' && window.matchMedia) {
    if (window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
  }
  return 'light';
}

// Apply synchronously at module load so the first paint already has the right
// theme (avoids a light→dark flash on reload for dark-mode users).
if (typeof document !== 'undefined') {
  document.documentElement.dataset.theme = getInitialTheme();
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (_) {
      /* ignore persistence failures */
    }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === 'dark' ? 'dark' : 'light');
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, isDark: theme === 'dark', setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside <ThemeProvider>');
  }
  return ctx;
}
